import React, { useState } from "react";
import Loader from "../../../components/Loader";
import { toast } from "react-toastify";
import { X } from "lucide-react";

const PAYMENT_MODES = ["Cash", "Card", "UPI", "Other"];

const BillSummary = ({
  orderId,
  mode,
  cart = [],
  subtotal = 0,
  taxPercent = 0,
  taxAmount = 0,
  discount = 0,
  setDiscount,
  selectedClientData = null,
  loyaltySettings = null,
  orderDate,
  onPayAndPrint,
  onPayLater,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [redeemPoints, setRedeemPoints] = useState(false);
    // Fallback to local state if setDiscount is not provided
    // const [localDiscount, setLocalDiscount] = useState(propDiscount);
    // const effectiveDiscount = typeof setDiscount === "function" ? propDiscount : localDiscount;
  // Payment modes state
  const inputClass =
    "w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-800 " +
    "focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 transition"

  const [payments, setPayments] = useState([
    { mode: "Cash", amount: "" }
  ]);

  // Add new payment mode
  const addPaymentMode = () => {
    setPayments([...payments, { mode: "Cash", amount: "" }]);
  };

  // Remove payment mode
  const removePaymentMode = (idx) => {
    setPayments(payments.filter((_, i) => i !== idx));
  };

  // Update payment mode or amount
  const updatePayment = (idx, field, value) => {
    setPayments(payments.map((p, i) =>
      i === idx ? { ...p, [field]: value } : p
    ));
  };

  // Calculate total paid
  const totalPaid = payments.reduce((sum, p) => sum + Number(p.amount || 0), 0);

  // Calculate loyalty points discount
  let pointsDiscount = 0;
  const availablePoints = selectedClientData?.loyaltyPoints || 0;
  if (redeemPoints && availablePoints > 0) {
    pointsDiscount = availablePoints * (loyaltySettings?.pointValue || 1);
  }

  // Calculate total after discount
  const computedTotal = Number(Math.max(0, subtotal - discount - pointsDiscount).toFixed(2));

  return (
    <div className="w-full h-full bg-white flex flex-col text-xs">

      {/* ===== HEADER ===== */}
      <div className="flex justify-between bg-blue-50 items-center px-2 pt-2 pb-3 border-b">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">Bill Summary</h2>
            {orderId && (
              <span className="text-xs text-gray-700 font-medium">| Order ID: <span className="text-gray-900 font-bold">{orderId}</span></span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Date: <span className="font-medium text-slate-700">{orderDate ? new Date(orderDate).toLocaleDateString('en-GB') : new Date().toLocaleDateString('en-GB')}</span>
          </div>
        </div>

        <button onClick={onClose} className="self-start mt-1 mr-1">
          <X size={18} />
        </button>
      </div>

      {/* ===== CLIENT INFO ===== */}
      <div className="p-2 border-b">
        <div className="font-semibold mb-1">Customer Details</div>
        {selectedClientData ? (
          <>
            <div>{selectedClientData.name}</div>
            <div className="text-gray-500 text-xs">
              {selectedClientData.phone}
            </div>
          </>
        ) : (
          <div className="text-gray-400 text-xs">
            Walk-in Customer
          </div>
        )}
      </div>

      {/* ===== ITEMS ===== */}
      <div className="flex-1 overflow-y-auto p-2">
        {cart.length === 0 ? (
          <div className="text-center text-gray-400 text-sm">
            No items in cart
          </div>
        ) : (
          cart.map((item) => {
            // Prefer manualAmount if present, fallback to price
            const defaultPrice = item.taxInclude === false ? (item.priceWithGst || item.price) : item.price;
            const displayPrice = item.manualAmount !== null && !isNaN(item.manualAmount) ? item.manualAmount : defaultPrice;
            return (
              <div
                key={item._id}
                className="flex justify-between mb-3 text-sm"
              >
                <div>
                  <div className="font-medium">{item.name}</div>
                  <div className="text-xs text-gray-500">
                    {item.qty} {item.quantityValue && item.quantityUnit && (
                      <span>({item.quantityValue}{item.quantityUnit})</span>
                    )} × ₹{Number(displayPrice).toFixed(2)}
                    <div className="text-[9px] mt-0.5">
                      Base: ₹{(((item.qty * displayPrice) * 100) / (100 + (item.taxRate ?? 0))).toFixed(2)} | GST: ₹{(((item.qty * displayPrice) * (item.taxRate ?? 0)) / (100 + (item.taxRate ?? 0))).toFixed(2)}
                    </div>
                  </div>
                </div>

                <div className="font-semibold">
                  ₹{(item.qty * displayPrice).toFixed(2)}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* ===== TOTAL SECTION ===== */}
      

   
<div className="border-t p-2  space-y-2">

  <div className="flex justify-between">
    <span>Subtotal (Excl. GST)</span>
    <span>₹{cart.reduce((sum, item) => {
      const defaultPrice = item.taxInclude === false ? (item.priceWithGst || item.price) : item.price;
      const displayPrice = item.manualAmount !== null && !isNaN(item.manualAmount) ? item.manualAmount : defaultPrice;
      const rate = item.taxRate ?? 0;
      return sum + ((item.qty * displayPrice) * 100 / (100 + rate));
    }, 0).toFixed(2)}</span>
  </div>

  <div className="flex justify-between text-gray-500">
    <span>Total GST</span>
    <span>₹{cart.reduce((sum, item) => {
      const defaultPrice = item.taxInclude === false ? (item.priceWithGst || item.price) : item.price;
      const displayPrice = item.manualAmount !== null && !isNaN(item.manualAmount) ? item.manualAmount : defaultPrice;
      const rate = item.taxRate ?? 0;
      return sum + ((item.qty * displayPrice) * rate / (100 + rate));
    }, 0).toFixed(2)}</span>
  </div>

  <div className="flex justify-between items-center">
    <span>Discount</span>
    <input
      type="number"
      min="0"
      max={subtotal}
      value={discount}
      onChange={e => {
        const val = Number(e.target.value) || 0;
        if (val > subtotal) {
          toast.error("Discount cannot exceed subtotal");
          return;
        }
        setDiscount(val);
      }}
      className="w-30 border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition text-right"
      placeholder="0"
    />
  </div>

  {/* LOYALTY POINTS REDEMPTION */}
  {loyaltySettings?.enabled && selectedClientData && (selectedClientData.loyaltyPoints || 0) > 0 && (
    <div className="flex justify-between items-center bg-blue-50 p-2 rounded-md border border-blue-100 mt-2">
      <div className="flex items-center gap-2">
        <input 
          type="checkbox" 
          id="redeemPoints"
          checked={redeemPoints}
          onChange={(e) => setRedeemPoints(e.target.checked)}
          className="w-4 h-4 accent-blue-600"
        />
        <label htmlFor="redeemPoints" className="text-blue-900 font-medium cursor-pointer">
          Redeem {selectedClientData.loyaltyPoints} Points
        </label>
      </div>
      <span className="text-blue-900 font-bold">
        -₹{Number(selectedClientData.loyaltyPoints * (loyaltySettings?.pointValue || 1)).toFixed(2)}
      </span>
    </div>
  )}

  {/* <div className="flex justify-between items-center">
    <span>GST ({taxPercent}%)</span>
    <input
      type="number"
      min="0"
      value={taxAmount}
      onChange={e => typeof setTaxAmount === 'function' && setTaxAmount(Number(e.target.value) || 0)}
      className={inputClass}
      placeholder="0"
      style={{ width: 70 }}
    />
  </div> */}

  <div className="flex justify-between text-lg font-bold border-t pt-2">
    <span>Final Total</span>
    <span>₹{Number(computedTotal).toFixed(2)}</span>
  </div>
</div>


{/* ===== PAYMENT SECTION ===== */}

<div className="border-t px-3 pt-1 pl- space-y-1">
  <div className="flex items-center justify-between mb-1">
    <span className="font-semibold text-xs">Payment Collection</span>
    <button
      type="button"
      className="text-blue-900 text-xs font-medium ml-2"
      onClick={addPaymentMode}
    >
      + Add Payment Mode
    </button>
  </div>
  {payments.map((p, idx) => (
    <div key={idx} className="flex gap-1 items-center mb-1">
      <select
        className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full"
        value={p.mode}
        onChange={e => updatePayment(idx, 'mode', e.target.value)}
        style={{ height: '40px', minWidth: '120px' }}
      >
        {PAYMENT_MODES.map(mode => (
          <option key={mode} value={mode}>{mode}</option>
        ))}
      </select>
      <input
        type="number"
        min="0"
        className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full"
        placeholder="Amount"
        value={p.amount}
        onChange={e => updatePayment(idx, 'amount', e.target.value)}
        style={{ height: '40px', minWidth: '120px' }}
      />
      {payments.length > 1 && (
        <button
          type="button"
          className="text-red-500 text-xs font-bold px-1"
          onClick={() => removePaymentMode(idx)}
        >
          ×
        </button>
      )}
    </div>
  ))}


  <div className="border-t pt-2 pb-2 mt-2">
    <div className="flex justify-between text-green-600 font-bold text-xl">
      <span>Paid</span>
      <span>₹{totalPaid}</span>
    </div>
    {mode === "credit" && (
      <div className={`flex justify-between font-semibold ${
        computedTotal - totalPaid > 0 ? "text-red-600" : "text-green-600"
      }`}>
        <span>Balance</span>
        <span>₹{Math.max(0, computedTotal - totalPaid)}</span>
      </div>
    )}
  </div>

</div>


      {/* ===== ACTION BUTTONS ===== */}
      <div className="p-2 border-t">
        <div className="flex gap-2 flex-nowrap">
          {mode === "cash" && (
            <button
              onClick={async () => {
                if (loading) return;
                setLoading(true);
                if (totalPaid < computedTotal) {
                  toast.error("Insufficient payment");
                  setLoading(false);
                  return;
                }
                await onPayAndPrint(totalPaid, { redeemedPoints: redeemPoints ? (selectedClientData?.loyaltyPoints || 0) : 0, pointsDiscount });
                setLoading(false);
              }}
              className="flex-1 bg-orange-500 hover:bg-amber-500 text-white py-2 px-2 rounded-md font-medium text-sm transition flex items-center justify-center gap-2"
              disabled={loading}
            >
              {loading ? <Loader size={18} inline/> : "Confirm Payment"}
            </button>
          )}
          {mode === "credit" && (
            <button
              onClick={async () => {
                if (loading) return;
                setLoading(true);
                await onPayLater(payments, { redeemedPoints: redeemPoints ? (selectedClientData?.loyaltyPoints || 0) : 0, pointsDiscount });
                setLoading(false);
              }}
              className="flex-1 bg-orange-500 hover:bg-amber-500 text-white py-2 px-2 rounded-md font-medium text-sm transition flex items-center justify-center gap-2"
              disabled={loading}
            >
              {loading ? <Loader size={18} inline /> : "Save as Credit"}
            </button>
          )}
          <button
            onClick={onClose}
            className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-6 py-2 rounded-md transition-colors text-sm border border-slate-200"
          >
            Cancel
          </button>
        </div>
      </div>

    </div>
  );
};

export default BillSummary;
